import { apiClient, handleApiError, isBackendEnabled } from '../client';
import {
    ApiResponse,
    ProductsResponse,
    ProductResponse,
    GetProductsParams,
    ProductLocationResponse,
} from '../types';
import { RawProduct } from '../../types';
import { mockProductsApi } from '../mock';

/**
 * Products API Service
 * Handles all product-related API calls
 * Uses backend API if enabled, otherwise uses mock/local data
 */
export const productsApi = {
    /**
     * Get all products with optional filtering and pagination
     */
    getProducts: async (params?: GetProductsParams): Promise<ProductsResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockProductsApi.getProducts(params);
        }

        try {
            // Backend returns array directly, not wrapped in ApiResponse
            const response = await apiClient.get<RawProduct[]>('/product', {
                params,
            });
            // Transform array response to ProductsResponse format
            const products = response.data;
            return {
                products,
                total: products.length,
                page: params?.page || 1,
                limit: params?.limit || products.length,
                totalPages: params?.limit ? Math.ceil(products.length / (params.limit || products.length)) : 1,
            };
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get a single product by ID
     */
    getProductById: async (productId: string): Promise<ProductResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockProductsApi.getProductById(productId);
        }

        try {
            const response = await apiClient.get<ApiResponse<ProductResponse>>(
                `/products/${productId}`
            );
            return response.data.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Search products by query string
     */
    searchProducts: async (query: string, params?: Omit<GetProductsParams, 'search'>): Promise<ProductsResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockProductsApi.searchProducts(query, params);
        }

        try {
            const response = await apiClient.get<ApiResponse<ProductsResponse>>('/products/search', {
                params: {
                    ...params,
                    q: query,
                },
            });
            return response.data.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get products by category
     */
    getProductsByCategory: async (
        category: string,
        params?: Omit<GetProductsParams, 'category'>
    ): Promise<ProductsResponse> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockProductsApi.getProductsByCategory(category, params);
        }

        try {
            const response = await apiClient.get<ApiResponse<ProductsResponse>>(
                `/products/category/${category}`,
                {
                    params,
                }
            );
            return response.data.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get a single product by EAN/barcode
     * GET /product/ean/{ean}
     */
    getProductByEan: async (ean: string): Promise<RawProduct> => {
        if (!isBackendEnabled() || !apiClient) {
            // Fallback to mock: search local products by EAN
            const { products } = await mockProductsApi.getProducts({ limit: 10000 });
            const found = products.find((p) => p.EAN === ean);
            if (!found) {
                throw new Error(`Product with EAN ${ean} not found`);
            }
            return found;
        }

        try {
            const response = await apiClient.get<RawProduct>(
                `/product/ean/${encodeURIComponent(ean)}`
            );
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get all available categories
     */
    getCategories: async (): Promise<string[]> => {
        if (!isBackendEnabled() || !apiClient) {
            return mockProductsApi.getCategories();
        }

        try {
            const response = await apiClient.get<ApiResponse<string[]>>('/products/categories');
            return response.data.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    /**
     * Get product location in a specific store
     * GET /product/{product_id}/location?store_id={store_id}
     */
    getProductLocation: async (
        productId: string,
        storeId?: string
    ): Promise<ProductLocationResponse | null> => {
        if (!isBackendEnabled() || !apiClient) {
            return null;
        }

        try {
            const response = await apiClient.get<ProductLocationResponse>(
                `/product/${encodeURIComponent(productId)}/location`,
                {
                    params: storeId ? { store_id: storeId } : undefined,
                }
            );
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },
};

